import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Lock, Eye, Trash2, ShieldCheck } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const pillars = [
  {
    Icon: Eye,
    title: 'Tu vois tout ce que Skipya voit',
    body: 'Aucune donnée n’est analysée en arrière-plan sans que tu la vois d’abord. Tes documents, tes réponses, tes calculs — tout est visible dans ton espace.',
  },
  {
    Icon: ShieldCheck,
    title: 'Tu valides chaque action',
    body: 'Skipya ne déclare rien à ta place. Aucun envoi vers impots.gouv, la CAF ou quiconque sans ton accord explicite. Tu restes l’unique décideur.',
  },
  {
    Icon: Trash2,
    title: 'Suppression en un clic',
    body: 'Tu peux effacer ton compte et toutes tes données à tout moment depuis ton profil. Suppression définitive, sans condition.',
  },
  {
    Icon: Lock,
    title: 'Stockage sécurisé',
    body: 'Tes documents et informations sont chiffrés au repos et en transit. Aucun partage avec des tiers à des fins publicitaires.',
  },
];

export const TrustControlSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="trust-control"
      className={`section-apple px-6 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}
      data-testid="trust-control-section"
    >
      <div className="max-w-[980px] mx-auto">
        <motion.div
          ref={ref}
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <p className="text-[13px] tracking-[0.02em] uppercase mb-4 text-[#81d8d0]">
            Tes données restent sous ton contrôle
          </p>
          <h2 className={`text-[34px] sm:text-[44px] font-semibold tracking-[-0.03em] leading-[1.1] mb-3 ${isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'}`}>
            C'est toi qui décides.<br />
            <span className="text-[#81d8d0]">Toujours.</span>
          </h2>
          <p className={`text-[15px] max-w-[600px] mx-auto ${isDark ? 'text-[#86868b]' : 'text-[#6e6e73]'}`}>
            Skipya s'appuie sur des sources publiques (Service-Public, OpenFisca, CAF, impots.gouv.fr).
            Aucune connexion automatique aux organismes — tu valides chaque démarche.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pillars.map(({ Icon, title, body }, i) => (
            <motion.div
              key={title}
              className={`p-6 rounded-2xl border ${isDark ? 'bg-[#141414] border-white/5' : 'bg-[#f5f5f7] border-gray-200'}`}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
              data-testid={`trust-pillar-${i}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#81d8d0]/10' : 'bg-[#81d8d0]/15'}`}>
                  <Icon size={18} strokeWidth={1.5} className="text-[#81d8d0]" />
                </div>
                <div className="flex-1">
                  <h3 className={`text-[16px] font-semibold mb-1 tracking-[-0.01em] ${isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'}`}>{title}</h3>
                  <p className={`text-[13px] leading-[1.5] ${isDark ? 'text-[#86868b]' : 'text-[#6e6e73]'}`}>{body}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustControlSection;
